import * as React from 'react';

import { CustomDialog } from 'components/Dialogs/CustomDialog';
import { IManager } from 'common/user/interfaces/manager';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { AnimatedLoaderModal } from 'components/Loaders/AnimatedLoader';

import styles from './styles.module.scss';
import { useMemo, useState } from 'react';
import { DocumentTypeEnum } from '../../../../common/document/enums/document-type.enum';
import {
  confirmRejectionSmsCDL,
  confirmRejectionSmsDOTMed,
  confirmRejectionSmsDriverChecklist,
  confirmGenericRejectionSms,
} from '../../../../common/notifications/sms/confirmRejectionSms';
import { DocumentRejectionReasonEnum } from '../../../../common/document/enums/document-rejection-reason.enum';
import OutlinedButton from '../../../../components/Buttons/OutlinedButton';
import { Textarea } from '../../../../components/Form/Textarea';
import { Checkbox } from '@mui/material';

type InputProps = {
  isLoading: boolean;
  isDelivered: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  rejectionReason: DocumentRejectionReasonEnum;
  rejectionDetails: string;
  documentType: string;
  candidateFullName: string;
  manager: IManager;
  companyName: string;
  link: string;
  onSendMessage: ({
    text,
    removeDocumentFromRequest,
  }: {
    text: string;
    removeDocumentFromRequest?: boolean;
  }) => void;
};

export const ConfirmRejectionDialog = ({
  isLoading = false,
  isDelivered = false,
  open = false,
  setOpen,
  rejectionReason,
  rejectionDetails,
  documentType,
  candidateFullName,
  manager,
  companyName,
  link,
  onSendMessage,
}: InputProps) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [removeDocumentFromRequest, setRemoveDocumentFromRequest] =
    useState<boolean>(false);

  const defaultMessageContent = useMemo(() => {
    const [driverName = ''] = candidateFullName.split(' ');

    const data = {
      driverName,
      managerName: manager.firstName,
      companyName,
      rejectionReason,
      rejectionDetails,
      link: '',
      documentType,
    };

    if (documentType === DocumentTypeEnum.DRIVER_LICENSE) {
      return confirmRejectionSmsCDL(data);
    }
    if (documentType === DocumentTypeEnum.DOT_MEDICAL) {
      return confirmRejectionSmsDOTMed(data);
    }
    if (documentType === DocumentTypeEnum.DRIVER_CHECKLIST) {
      return confirmRejectionSmsDriverChecklist(data);
    }
    return confirmGenericRejectionSms(data);
  }, [
    rejectionReason,
    documentType,
    candidateFullName,
    manager,
    companyName,
    link,
  ]);

  const defaultMessageFooter = useMemo(() => {
    return ` ${link} Reply STOP to unsubscribe. Msg & Data rates may apply.`;
  }, [link]);

  const [message, setMessage] = useState<string>(
    defaultMessageContent + defaultMessageFooter
  );
  const [messageTemp, setMessageTemp] = useState<string>(defaultMessageContent);

  const handleSendMessage = () => {
    onSendMessage({
      text: message.replaceAll(/(\s)+/gim, ' '),
      removeDocumentFromRequest,
    });
  };

  const handleChangeMessage = (e: any) => {
    setMessageTemp(e.target.value);
  };

  const handleSaveMessage = () => {
    setMessage(messageTemp + defaultMessageFooter);
    setIsEdit(false);
  };

  return (
    <CustomDialog open={open} setOpen={setOpen} title="Confirm Rejection">
      {isLoading ? (
        <AnimatedLoaderModal
          isLoading={!isDelivered}
          classNameWrapper={styles.loaderWrapper}
        />
      ) : (
        <div className={styles.root}>
          {isEdit ? (
            <div className={styles.editRoot}>
              <Textarea
                minRows={5}
                maxLength={200}
                value={messageTemp}
                onChange={handleChangeMessage}
              />
              <div className={styles.message}>
                {messageTemp}
                <br />
                <span className={styles.link}>{link}</span> Reply STOP to
                unsubscribe. Msg & Data rates may apply.
              </div>
              <div className={styles.actions}>
                <OutlinedButton
                  onClick={() => {
                    setIsEdit(false);
                    setMessageTemp(defaultMessageContent);
                  }}
                >
                  Cancel
                </OutlinedButton>
                <ButtonWithArrowIcon onClick={handleSaveMessage}>
                  Save
                </ButtonWithArrowIcon>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.description}>
                Upon confirming your rejection of the License, the following
                message will be sent to {candidateFullName}:
              </div>
              <div className={styles.message}>{message}</div>
              <div className={styles.settings}>
                <div className={styles.checkBoxWrapper}>
                  <Checkbox
                    checked={removeDocumentFromRequest}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setRemoveDocumentFromRequest(event.target.checked);
                    }}
                    sx={{
                      '& .MuiSvgIcon-root': { fontSize: 21 },
                      '&': { padding: '5px 5px 5px 0px' },
                    }}
                  />
                  <div>Remove request, allow driver to start over</div>
                </div>
              </div>
              <div className={styles.actions}>
                <OutlinedButton
                  onClick={() => {
                    setIsEdit(true);
                  }}
                >
                  Edit Message
                </OutlinedButton>
                <ButtonWithArrowIcon onClick={handleSendMessage}>
                  Confirm Rejection
                </ButtonWithArrowIcon>
              </div>
            </>
          )}
        </div>
      )}
    </CustomDialog>
  );
};
